import { useEffect, useState } from 'react';
import api from '../../../services/api';
import type { BoardroomNotice, BoardroomReport, BoardroomReportKey } from '../../../types';
import CompetitorsReport from './CompetitorsReport';
import LandscapeReport from './LandscapeReport';
import WhoRanksReport from './WhoRanksReport';
import ProfilePane from './ProfilePane';
import type { OnDig } from './MissingList';

const NO_DIG: OnDig = () => {};

/**
 * What a noticing would put on the board, shown before anyone decides.
 *
 * LOOK, THEN CHOOSE. The tray offered "Put it on the board" or "Not
 * interesting" off one line, and the card only existed once it was taken.
 * This draws the same view the card would be - the pain's own page, or the
 * table cut by the noticing's filter - without making anything. Read only: a
 * preview is not where work starts.
 */
export default function NoticePreview({
  boardroomId,
  notice,
  onOpen,
  onEdit,
  roomName,
}: {
  boardroomId: number;
  notice: BoardroomNotice;
  onOpen: (itemId: number) => void;
  onEdit: (itemId: number) => void;
  roomName?: string;
}) {
  const card = notice.card;
  const [data, setData] = useState<BoardroomReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (card.kind === 'profile') return;
    let alive = true;
    setData(null);
    setError(null);
    api
      .getBoardroomReport<BoardroomReport>(boardroomId, card.kind as BoardroomReportKey, {
        tags: card.lens?.tags ?? [],
        verified: card.lens?.verified ?? null,
        b: card.b ?? null,
      })
      .then(d => alive && setData(d))
      .catch(() => alive && setError('Could not draw this one.'));
    return () => {
      alive = false;
    };
  }, [boardroomId, card.kind, card.b, card.lens?.tags, card.lens?.verified]);

  if (card.kind === 'profile') {
    return (
      <ProfilePane
        boardroomId={boardroomId}
        subject={card.subject}
        onOpen={onOpen}
        onEdit={onEdit}
        onDig={NO_DIG}
        digging={false}
      />
    );
  }
  if (error) return <p className="text-xs text-red-600 dark:text-red-400">{error}</p>;
  if (!data) return <p className="text-xs text-gray-400">Reading the board…</p>;
  if (data.kind === 'competitors') {
    return <CompetitorsReport data={data} onOpen={onOpen} onDig={NO_DIG} digging={false} roomName={roomName} />;
  }
  if (data.kind === 'landscape') {
    return <LandscapeReport data={data} onOpen={onOpen} onDig={NO_DIG} digging={false} roomName={roomName} />;
  }
  if (data.kind === 'who_ranks') return <WhoRanksReport data={data} />;
  return <p className="text-xs text-gray-500 dark:text-gray-400">There is no preview for this kind of card yet.</p>;
}
